import React from 'react';
import { updateSlideAnalytics, sendSlideIdUpdate } from '@services';
import { getAvailableTerms } from './creditAnalysisHelpers';
import { ANALYTICS_EVENTS, BROWSER_STORAGE_KEYS, Profile } from '@constants';
import { selectTerm, setAvailableTerms, setLastSlideId, setSlideId } from '@actions/creators';
import { getBrowserStorageItem, setBrowserStorageItem } from './browserStorage';
import { 
    Introduction, 
    YourAnalysis, 
    WeCanHelp, 
    Modification, 
    TermOptions,
    Summary, 
    AdditionalInfo,  
    Fico, 
    CreditScore, 
    InterestCalculator,
    FinancialPath, 
    LoanCalculator, 
    DTI,
    CompanyYouCanTrust,
    ClientPortal,
    ClientTestimonials } from '@components';

export const renderCurrentSlide = (name, clientViewing) => {
    switch(name) {
        case 'Introduction':
            return <Introduction />;
        case 'Your Analysis':
            return <YourAnalysis />;
        case 'We Can Help':
            return <WeCanHelp />;
        case 'Term Options':
            return <TermOptions />;
        case 'Modification':
            return <Modification />;
        case 'Additional Info':
            return <AdditionalInfo />;
        case 'Summary':
            return <Summary />;
        case 'Fico':
            return <Fico />;
        case 'Credit Score':
            return <CreditScore />;
        case 'Interest Calculator':
            return <InterestCalculator />;
        case 'Financial Path':
            return <FinancialPath />;
        case 'Loan Calculator':
            return <LoanCalculator />;
        case 'DTI':
            return <DTI />;
        case 'A Company You Can Trust':
            return <CompanyYouCanTrust />;
        case 'Client Portal':
            return <ClientPortal />;
        case 'Client Testimonials':
            return <ClientTestimonials />;
        default: {
            const msg = 'A component for this slide has not yet been added.';
            return clientViewing ? msg : `${msg} Make sure to add your new slide to the renderCurrentSlide() utility function.`;
        }
    }
};

export const updateCurrentSlide = async (dispatch, newSlideId, lastSlideId, presentationId, profile) => {
    const { slideIdKey } = BROWSER_STORAGE_KEYS;
    const { CUSTOMER_SERVICE } = Profile;

    dispatch(setLastSlideId(lastSlideId));
    dispatch(setSlideId(newSlideId));
    setBrowserStorageItem(slideIdKey, newSlideId);

    if (profile !== CUSTOMER_SERVICE) {
        await sendSlideIdUpdate({ slideId: newSlideId, lastSlideId, presentationId });
    }
};

// Complex logic to handle all the possible combinations of slide CA data edge cases 
// ...when rep is editing data in Credit Analysis and values are changing dynamically
export const getCurrentTerm = (slideData, currentTermData) => {
    const { initialTermLoaded, setInitialTermLoaded, slideState, dispatch } = currentTermData;
    const { id, details: creditData } = slideData;
    const { selectedTermKey, maxTermKey } = BROWSER_STORAGE_KEYS;

    if (creditData && creditData.hasOwnProperty('maxTerm')) {
        const { maxTerm } = creditData;

        const savedTerm = getBrowserStorageItem(`${selectedTermKey}_${id}`);
        const previousMaxTerm = getBrowserStorageItem(`${maxTermKey}_${id}`);

        const availableTerms = getAvailableTerms(id, creditData);
        const selectedTerm = slideState.selectedTerm[id];
        const termLoaded = initialTermLoaded[id];
        
        const maxTermHasChanged = previousMaxTerm && previousMaxTerm !== maxTerm;
        const savedPositionIsValid = savedTerm && availableTerms.includes(savedTerm);
        const currentOptionIsValid = availableTerms.includes(selectedTerm);

        if (!termLoaded) {
            if (savedPositionIsValid && !maxTermHasChanged) {
                dispatch(selectTerm(savedTerm, id));
                setInitialTermLoaded({ ...initialTermLoaded, [id]: true });
            } else {
                dispatch(selectTerm(maxTerm, id));
                setInitialTermLoaded({ ...initialTermLoaded, [id]: true });
                setBrowserStorageItem(`${maxTermKey}_${id}`, maxTerm);
            }

            dispatch(setAvailableTerms(availableTerms));
        } else if (termLoaded) {
            if (!currentOptionIsValid || maxTermHasChanged) {
                dispatch(selectTerm(maxTerm, id));
                dispatch(setAvailableTerms(availableTerms));

                setBrowserStorageItem(`${selectedTermKey}_${id}`, maxTerm);
                setBrowserStorageItem(`${maxTermKey}_${id}`, maxTerm);
            }
        }  
    }
};
